import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const fmt = (n) => (n || 0).toLocaleString();

const Delta = ({ cur, prev, id }) => {
  if (prev == null) return null;
  if (!prev) return <span className="text-xs text-neutral-400" data-testid={`ai-month-delta-${id}`}>{cur ? 'new' : '—'}</span>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const Icon = pct >= 0 ? ArrowUpRight : ArrowDownRight;
  return <span className={`text-xs inline-flex items-center ${pct > 0 ? 'text-orange-700' : pct < 0 ? 'text-green-700' : 'text-neutral-500'}`} title={`Previous: ${fmt(prev)}`} data-testid={`ai-month-delta-${id}`}><Icon className="w-3 h-3" />{Math.abs(pct)}%</span>;
};

const Tile = ({ label, cur, prev, id }) => (
  <div className="bg-white border border-neutral-200 rounded-lg px-4 py-3">
    <p className="text-xs text-neutral-500">{label}</p>
    <div className="flex items-baseline gap-2"><p className="text-xl font-semibold text-neutral-900" data-testid={`ai-month-${id}`}>{fmt(cur)}</p><Delta cur={cur} prev={prev} id={id} /></div>
  </div>
);

const Breakdown = ({ title, rows, prevRows, keyOf, testId, showRequests }) => {
  const max = Math.max(1, ...rows.map((r) => r.tokens));
  const prevOf = (k) => (prevRows ? (prevRows.find((r) => keyOf(r) === k) || { tokens: 0 }).tokens : null);
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-3">{title}</p>
      {rows.length ? rows.map((r) => (
        <div key={keyOf(r)} className="mb-2.5" data-testid={`${testId}-${keyOf(r).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
          <div className="flex justify-between text-sm"><span>{r.label || r.feature}</span>
            <span className="tabular-nums text-neutral-700">{fmt(r.tokens)}{showRequests && <span className="text-neutral-400"> · {fmt(r.requests)} req</span>} <Delta cur={r.tokens} prev={prevOf(keyOf(r))} id={`${testId}-${keyOf(r)}`} /></span></div>
          <div className="h-1.5 bg-neutral-100 rounded mt-1"><div className="h-1.5 bg-amber-500 rounded" style={{ width: `${(r.tokens / max) * 100}%` }} /></div>
        </div>
      )) : <p className="text-sm text-neutral-400">No requests this month.</p>}
    </div>
  );
};

// Calendar-month (New York time) token summary built from the 90-day usage log.
export const AiMonthlySummary = ({ api }) => {
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.aiUsageMonthly(month || undefined).then(setData).catch(() => toast.error('Could not load monthly AI usage')); }, [api, month]);
  if (!data) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>;
  const c = data.current, p = data.previous;
  return (
    <div className="space-y-3" data-testid="ai-monthly-summary">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h3 className="font-medium text-neutral-900">Monthly usage — {data.label}{data.is_current && <span className="text-neutral-400 font-normal"> (so far)</span>}</h3>
          <p className="text-sm text-neutral-500" data-testid="ai-month-compare-note">{p ? <>Compared with {p.label}{p.partial && ' (partly outside the 90-day log)'}.</> : 'No data for the previous month to compare.'} Months run midnight to midnight, New York time.</p></div>
        <select className="h-9 border border-neutral-300 bg-white px-2 text-sm" value={data.month} onChange={(e) => setMonth(e.target.value)} data-testid="ai-month-select">
          {data.months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Tile id="tokens" label="Total tokens" cur={c.tokens} prev={p?.tokens} />
        <Tile id="requests" label="Requests" cur={c.requests} prev={p?.requests} />
        <Tile id="quality" label="Quality model (Opus)" cur={c.quality} prev={p?.quality} />
        <Tile id="fallbacks" label="Fallbacks" cur={c.fallbacks} prev={p?.fallbacks} />
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <Breakdown title="Tokens by feature" rows={c.by_feature} prevRows={p?.by_feature} keyOf={(r) => r.feature} testId="ai-month-by-feature" />
        <Breakdown title="Tokens & requests by model" rows={c.by_model} prevRows={p?.by_model} keyOf={(r) => r.model} testId="ai-month-by-model" showRequests />
      </div>
    </div>
  );
};
